import { CubeFilterDimensions } from 'cube/components/CubeReportFilterRow'

import { ReportPage } from 'types/pages'

/**
 * Reporting pages supported by the cube reporting infra
 */
export type CubeReportPage =
  | ReportPage.Conversation
  | ReportPage.Routing
  | ReportPage.Service
  | ReportPage.ChatDetail
  | ReportPage.ChatOverview
  | ReportPage.RevenueOpportunities
  | ReportPage.SiteConcierge
  | ReportPage.AIInsights
  | ReportPage.AIPerformanceOverview

/**
 * Page-level filters supported for each cube report page
 */
export const REPORT_TYPE_TO_FILTER_MAP: Record<
  CubeReportPage,
  CubeFilterDimensions[]
> = Object.freeze({
  [ReportPage.Conversation]: [
    CubeFilterDimensions.CONVERSATION_PLAYBOOK_ID,
    CubeFilterDimensions.TEAM_PARTICIPATED_TEAM_ID,
    CubeFilterDimensions.USER_PARTICIPATED_USER_ID,
    CubeFilterDimensions.DAY_OF_WEEK,
    CubeFilterDimensions.HOUR_OF_DAY,
    CubeFilterDimensions.END_USER_COUNTRY_CODE,
    CubeFilterDimensions.CONVERSATION_CLASSIFICATION_ID,
    CubeFilterDimensions.CONVERSATION_INBOX_ID,
    CubeFilterDimensions.CONVERSATION_PARTICIPANT_STATUS,
    CubeFilterDimensions.INITIAL_ROUTED_IN_USER,
    CubeFilterDimensions.INITIAL_ROUTED_IN_TEAM_ID,
    CubeFilterDimensions.AI_TOPIC_TRIGGERED,
    CubeFilterDimensions.NODE_TRIGGERED,
  ],
  [ReportPage.Routing]: [
    CubeFilterDimensions.CONVERSATION_PLAYBOOK_ID,
    CubeFilterDimensions.TEAM_PARTICIPATED_TEAM_ID,
    CubeFilterDimensions.USER_PARTICIPATED_USER_ID,
    CubeFilterDimensions.DAY_OF_WEEK,
    CubeFilterDimensions.HOUR_OF_DAY,
    CubeFilterDimensions.END_USER_COUNTRY_CODE,
    CubeFilterDimensions.CONVERSATION_CLASSIFICATION_ID,
    CubeFilterDimensions.CONVERSATION_INBOX_ID,
    CubeFilterDimensions.CONVERSATION_PARTICIPANT_STATUS,
    CubeFilterDimensions.INITIAL_ROUTED_IN_USER,
    CubeFilterDimensions.INITIAL_ROUTED_IN_TEAM_ID,
  ],
  [ReportPage.ChatDetail]: [
    // NOTE: Chat Data report
    CubeFilterDimensions.CONVERSATION_PLAYBOOK_ID,
    CubeFilterDimensions.CONVERSATION_TAG_NAME,
    CubeFilterDimensions.CONVERSATION_CLASSIFICATION_ID,
    CubeFilterDimensions.CONVERSATION_PARTICIPANT_STATUS,
    CubeFilterDimensions.CONVERSATION_INBOX_ID,
    CubeFilterDimensions.DAY_OF_WEEK,
    CubeFilterDimensions.HOUR_OF_DAY,
    CubeFilterDimensions.EMAIL_CAPTURED,
    CubeFilterDimensions.PHONE_CAPTURED,
    CubeFilterDimensions.MEETING_BOOKED,
    CubeFilterDimensions.CQL_SCORE,
    CubeFilterDimensions.LANGUAGE_DETECTED,
    CubeFilterDimensions.END_USER_COUNTRY_CODE,
    CubeFilterDimensions.CONVERSATION_STARTED_FROM_URL,
    CubeFilterDimensions.USER_PARTICIPATED_USER_ID,
    CubeFilterDimensions.TEAM_PARTICIPATED_TEAM_ID,
    CubeFilterDimensions.INITIAL_ROUTED_IN_USER,
    CubeFilterDimensions.INITIAL_ROUTED_IN_TEAM_ID,
    CubeFilterDimensions.CONVERSATION_HAD_TEAMMATE_ROUTED_IN,
    CubeFilterDimensions.CONVERSATION_ROUTING_OUTCOME,
    CubeFilterDimensions.TEAMMATES_UNAVAILABLE,
    CubeFilterDimensions.TEAMMATE_RESPONDED,
    CubeFilterDimensions.TEAMMATE_RESPONDED_LATE,
    CubeFilterDimensions.TEAMMATE_ROUTED_IN_TO_RESPONDED_WITHIN60S,
    CubeFilterDimensions.IS_DEAD_END_CONVERSATION,
    CubeFilterDimensions.CONVERSATION_ROUTED,
    CubeFilterDimensions.AI_TOPIC_TRIGGERED,
    CubeFilterDimensions.EXCLUDED_AI_TOPIC_TRIGGERED,
    CubeFilterDimensions.HAS_ROUTED_AND_EVERYONE_MISSED,
    CubeFilterDimensions.TIME_TO_FIRST_RESPONSE_S,
    CubeFilterDimensions.CHAT_INTERACTIONS_MESSAGE_BODY,
    CubeFilterDimensions.END_USER_COMPANY_NAME,
    CubeFilterDimensions.END_USER_EMAIL,
    CubeFilterDimensions.HAS_CALENDAR_DROP,
    CubeFilterDimensions.PLAYBOOK_TYPE,
    CubeFilterDimensions.CONVERSATION_HAS_UNRESPONSIVE_FALLBACK,
    CubeFilterDimensions.NODE_TRIGGERED,
    CubeFilterDimensions.SCENARIO_TRIGGERED,
    CubeFilterDimensions.URL_QUERY_PARAMETERS_PARAM_KEY_VALUE,
    CubeFilterDimensions.MEETING_STATUS,
    CubeFilterDimensions.IS_END_USER_MOBILE,
    CubeFilterDimensions.BUYER_SENTIMENT,
    CubeFilterDimensions.CSAT_RATING,
    CubeFilterDimensions.TOPIC_DETECTED_ID,
    CubeFilterDimensions.OPPORTUNITY_NAME,
    CubeFilterDimensions.BIONIC_MESSAGE_CATEGORY_ID,
  ],
  [ReportPage.Service]: [],
  [ReportPage.ChatOverview]: [
    CubeFilterDimensions.GEO_COUNTRY_CODE,
    CubeFilterDimensions.PLAYBOOK_METRICS_IS_END_USER_MOBILE,
  ],
  [ReportPage.RevenueOpportunities]: [
    CubeFilterDimensions.ROI_IS_INFLUENCED,
    CubeFilterDimensions.ROI_IS_SOURCED,
    CubeFilterDimensions.ROI_OPPORTUNITY_TYPE,
    CubeFilterDimensions.ROI_COMPANY,
    CubeFilterDimensions.ROI_CLOSED_DATE,
    CubeFilterDimensions.ROI_OPPORTUNITY_AMOUNT,
    CubeFilterDimensions.ROI_DAYS_IN_CYCLE,
    CubeFilterDimensions.ROI_CONTACT_COUNT,
    CubeFilterDimensions.ROI_OPPORTUNITY_STAGE,
    CubeFilterDimensions.OPPORTUNITY_NAME,
    CubeFilterDimensions.CONVERSATION_ROUTING_OUTCOME,
  ],
  [ReportPage.SiteConcierge]: [
    CubeFilterDimensions.END_USER_COUNTRY_CODE,
    CubeFilterDimensions.SITE_CONCIERGE_IS_END_USER_MOBILE,
  ],
  [ReportPage.AIInsights]: [
    CubeFilterDimensions.END_USER_COUNTRY_CODE,
    CubeFilterDimensions.IS_END_USER_MOBILE,
    CubeFilterDimensions.LANGUAGE_DETECTED,
    CubeFilterDimensions.GPT_ENABLED_PLAYBOOK_ID,
    CubeFilterDimensions.BIONIC_MESSAGE_CATEGORY_ID,
    CubeFilterDimensions.BIONIC_MESSAGE_SCENARIO_ID,
  ],
  [ReportPage.AIPerformanceOverview]: [
    CubeFilterDimensions.BIONIC_MESSAGE_CATEGORY_ID,
    CubeFilterDimensions.CONVERSATION_STARTED_FROM_URL,
    CubeFilterDimensions.URL_QUERY_PARAMETERS_PARAM_KEY_VALUE,
    CubeFilterDimensions.DAY_OF_WEEK,
    CubeFilterDimensions.HOUR_OF_DAY,
    CubeFilterDimensions.CONVERSATION_INBOX_ID,
    CubeFilterDimensions.BUYER_SENTIMENT,
    CubeFilterDimensions.CHAT_INTERACTIONS_MESSAGE_BODY,
    CubeFilterDimensions.END_USER_COMPANY_NAME,
    CubeFilterDimensions.END_USER_EMAIL,
    CubeFilterDimensions.IS_END_USER_MOBILE,
    CubeFilterDimensions.LANGUAGE_DETECTED,
    CubeFilterDimensions.CONVERSATION_PARTICIPANT_STATUS,
    CubeFilterDimensions.END_USER_COUNTRY_CODE,
    CubeFilterDimensions.CONVERSATION_TAG_NAME,
    CubeFilterDimensions.CONVERSATION_ROUTED,
    CubeFilterDimensions.CQL_SCORE,
    CubeFilterDimensions.CSAT_RATING,
    CubeFilterDimensions.EMAIL_CAPTURED,
    CubeFilterDimensions.HAS_CALENDAR_DROP,
    CubeFilterDimensions.CONVERSATION_CLASSIFICATION_ID,
    CubeFilterDimensions.MEETING_BOOKED,
    CubeFilterDimensions.MEETING_STATUS,
    CubeFilterDimensions.PHONE_CAPTURED,
    CubeFilterDimensions.CONVERSATION_HAD_TEAMMATE_ROUTED_IN,
    CubeFilterDimensions.GPT_ENABLED_PLAYBOOK_ID,
    CubeFilterDimensions.NODE_TRIGGERED,
    CubeFilterDimensions.SCENARIO_TRIGGERED,
  ],
})
